import { useState } from "react";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import { api, formatApiError, imgSrc } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const EMPTY = { name: "", description: "", price: "", category: "", features: [], image: "", order: 0, active: true, store: "speedmax", weight: 0.5, width: 15, height: 8, length: 20 };
const MUG_BOX = { weight: 0.4, width: 12, height: 12, length: 12 };

export const ProductForm = ({ initial, onClose, onSaved }) => {
  const [f, setF] = useState({ ...EMPTY, ...(initial.store === "idealize" ? MUG_BOX : {}), ...initial, price: initial.price || "", features: initial.features || [] });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/upload", fd);
      setF((s) => ({ ...s, image: data.url }));
      toast.success("Foto enviada");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    const body = { ...f, price: f.price.trim() || null, order: Number(f.order) || 0, features: f.features.filter(Boolean), weight: Number(f.weight) || 0.5, width: Number(f.width) || 15, height: Number(f.height) || 8, length: Number(f.length) || 20 };
    delete body.id; delete body.created_at;
    setBusy(true);
    try {
      if (initial.id) await api.put(`/admin/products/${initial.id}`, body);
      else await api.post("/admin/products", body);
      toast.success("Salvo!");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="admin-product-dialog" className="max-h-[90vh] overflow-y-auto bg-[#0e1120] text-white border-white/10">
        <DialogHeader><DialogTitle>{initial.id ? "Editar item" : "Novo item"} · {f.store === "idealize" ? "Idealize" : "Speed Max"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="h-24 w-24 shrink-0 overflow-hidden rounded-lg bg-black/40">{f.image && <img src={imgSrc(f.image)} alt="" className="h-full w-full object-cover" />}</div>
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-white/15 px-4 py-2 text-sm hover:bg-white/5">
              <Upload className="h-4 w-4" />{busy ? "Enviando..." : "Enviar foto"}
              <input data-testid="admin-image-input" type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} />
            </label>
          </div>
          <div><Label>Nome</Label><Input data-testid="admin-name-input" value={f.name} onChange={set("name")} required className="bg-black/30 border-white/15" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Preço (vazio = Consulte)</Label><Input data-testid="admin-price-input" placeholder="35,00" value={f.price} onChange={set("price")} className="bg-black/30 border-white/15" /></div>
            <div><Label>Categoria</Label><Input data-testid="admin-category-input" value={f.category} onChange={set("category")} className="bg-black/30 border-white/15" /></div>
          </div>
          <div><Label>Descrição</Label><Textarea data-testid="admin-description-input" value={f.description} onChange={set("description")} className="bg-black/30 border-white/15" /></div>
          <div><Label>Destaques (um por linha)</Label><Textarea data-testid="admin-features-input" value={f.features.join("\n")} onChange={(e) => setF({ ...f, features: e.target.value.split("\n") })} className="bg-black/30 border-white/15" /></div>
          <div>
            <Label>Frete: peso (kg) e caixa (cm)</Label>
            <div className="mt-1 grid grid-cols-4 gap-2">
              <Input data-testid="admin-weight-input" type="number" step="0.01" placeholder="kg" value={f.weight} onChange={set("weight")} className="bg-black/30 border-white/15" />
              <Input data-testid="admin-width-input" type="number" placeholder="larg." value={f.width} onChange={set("width")} className="bg-black/30 border-white/15" />
              <Input data-testid="admin-height-input" type="number" placeholder="alt." value={f.height} onChange={set("height")} className="bg-black/30 border-white/15" />
              <Input data-testid="admin-length-input" type="number" placeholder="comp." value={f.length} onChange={set("length")} className="bg-black/30 border-white/15" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 items-end">
            <div><Label>Ordem</Label><Input data-testid="admin-order-input" type="number" value={f.order} onChange={set("order")} className="bg-black/30 border-white/15" /></div>
            <label className="flex items-center gap-3 text-sm"><Switch data-testid="admin-active-switch" checked={f.active} onCheckedChange={set("active")} />Visível no site</label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button data-testid="admin-save-button" type="submit" disabled={busy} className="grad-bg text-white">Salvar</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
